import { Check, ClipboardCheck, ClipboardList, Plus } from "lucide-react";
import { useRef } from "react";

type RoundCheckButtonProps = {
  checked: boolean;
  onClick: () => void;
  /** "check" = 完了トグル（デフォルト）、"add" = +アイコン、"clipboard" = クリップボード */
  variant?: "check" | "add" | "clipboard";
};

const VARIANT_CONFIG = {
  check: {
    CheckedIcon: Check,
    UncheckedIcon: Check,
    checkedLabel: "未完了に戻す",
    uncheckedLabel: "完了にする",
    showIconWhenUnchecked: false,
    useRoundStyle: true,
  },
  add: {
    CheckedIcon: Plus,
    UncheckedIcon: Plus,
    checkedLabel: "追加を取り消す",
    uncheckedLabel: "買い物リストに追加",
    showIconWhenUnchecked: true,
    useRoundStyle: true,
  },
  clipboard: {
    CheckedIcon: ClipboardCheck,
    UncheckedIcon: ClipboardList,
    checkedLabel: "追加を取り消す",
    uncheckedLabel: "買い物リストに追加",
    showIconWhenUnchecked: true,
    useRoundStyle: false,
  },
} as const;

/** 丸いチェックボタン（ポヨンアニメーション付き） */
export const RoundCheckButton = ({
  checked,
  onClick,
  variant = "check",
}: RoundCheckButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const config = VARIANT_CONFIG[variant];

  const handleClick = () => {
    if (!checked && buttonRef.current?.animate) {
      buttonRef.current.animate(
        [
          { transform: "scale(1)" },
          { transform: "scale(1.3)" },
          { transform: "scale(0.9)" },
          { transform: "scale(1)" },
        ],
        { duration: 300, easing: "ease-out" },
      );
    }
    onClick();
  };

  const showIcon = checked || config.showIconWhenUnchecked;
  const Icon = checked ? config.CheckedIcon : config.UncheckedIcon;

  if (!config.useRoundStyle) {
    return (
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-all ${
          checked
            ? "border-primary bg-primary/15 text-primary"
            : "border-border text-muted-foreground"
        }`}
        aria-label={checked ? config.checkedLabel : config.uncheckedLabel}
      >
        <Icon className={checked ? "h-6 w-6" : "h-5 w-5"} />
      </button>
    );
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
        checked
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border text-muted-foreground"
      }`}
      aria-label={checked ? config.checkedLabel : config.uncheckedLabel}
    >
      {showIcon && <Icon className="h-4 w-4" />}
    </button>
  );
};
